import React, { useState, useRef, useEffect } from "react";
import BreadCrumb from "../../components/breadCrumb/BreadCrumb";
import NavBarWithoutLogo from "../../components/header/navaBarWithoutLogo";
import { useNavigate, useLocation } from "react-router-dom";
import { useVerifyOtp } from "../../common/services/otp/apiDanaConnect";
import { useAuth } from "../../common/provider/authContextType";
import Loading from "../../components/elements/loading";

interface OtpConfirmationPageProps {
  option: string;
}

const OtpConfirmationPage: React.FC<OtpConfirmationPageProps> = ({
  option,
}) => {
  const navigate = useNavigate();
  const [otp, setOtp] = useState(Array(6).fill(""));
  const [errors, setErrors] = useState(Array(6).fill(false));
  const [messageError, setMessageError] = useState('');
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);
  const location = useLocation();
  const [historyIndex, setHistoryIndex] = useState<number>(window.history.state?.idx || 0);
  const payload = location.state?.payload;
  const { expirationTime } = useAuth();
  const [timeRemaining, setTimeRemaining] = useState(expirationTime);
  const errorMessages = {
    "03": "El código ha expirado, por favor solicita uno nuevo",
    "04": "El código ingresado es incorrecto",
    "06": "¡Estamos teniendo problemas técnicos!  Por favor, intenta de nuevo.",
    "14": "Este código ya ha sido utilizado.",
    "16": "Ha superado el número máximo de intentos permitidos",
    "default": "¡Estamos teniendo problemas técnicos! Por favor, intenta de nuevo."
  };

  const {mutate: mutateVerifyOtp, isSuccess: isSuccessVerifyOtp, data: dataVerifyOtp, isError, isPending: isPendingVerifyOtp} = useVerifyOtp();

  const handleBack = () => {
    navigate(-1);
  };

  const resetOtp = () => {
    setOtp(Array(6).fill(''));
    setErrors(Array(6).fill(false));
    if (inputRefs.current[0]) {
      inputRefs.current[0].focus();
    }
  };

  useEffect(() => {
    const savedExpiration = localStorage.getItem('otpExpirationTime');
    if (savedExpiration) {
      const expirationTime = parseInt(savedExpiration, 10);
      const currentTime = Date.now();
      const remainingTime = Math.max(0, Math.floor((expirationTime - currentTime) / 1000));
      setTimeRemaining(remainingTime);
      if (remainingTime === 0) {
        setMessageError('El código ha expirado, por favor solicita uno nuevo');
      }
    } else {
      const expirationTime = Date.now() + (timeRemaining * 1000); // 120000 ms = 120 segundos
      localStorage.setItem('otpExpirationTime', expirationTime.toString());
      setTimeRemaining(expirationTime);
    }
  
    const intervalo = setInterval(() => {
      setTimeRemaining((prevTime: number) => {
        if (prevTime <= 1) {
          clearInterval(intervalo);
          setMessageError('El código ha expirado, por favor solicita uno nuevo');
          return 0;
        }
        const newTime = prevTime - 1;
        return newTime;
      });
    }, 1000);
  
    return () => clearInterval(intervalo);
  }, []);

  useEffect(() => {
    if (isSuccessVerifyOtp) {
      if(dataVerifyOtp?.responseCode === "01" && dataVerifyOtp?.responseDescription === "Success"){
        if (option === "forgotPassword") {
          navigate("/olvide-contrasena/paso-3---actualizar", { state: { payload } });
        } else if (option === "register") {
          navigate("/quiero-registrarme/paso-3-contrasena", { state: { payload } });
        }
      }else {
        setMessageError(errorMessages[dataVerifyOtp?.responseCode as keyof typeof errorMessages] || errorMessages["default"]);
        resetOtp();
      }
    }
  }, [isSuccessVerifyOtp]);

  useEffect(() => {
    const handlePopState = () => {
      const currentIndex = window.history.state?.idx || 0;

      if (currentIndex < historyIndex) {
        console.log("Retrocediendo en el historial");
        // Lógica para retroceder
      } else if (currentIndex > historyIndex) {
        console.log("Avanzando en el historial");
        // Lógica para avanzar
        if (location.pathname === '/olvide-contrasena/paso-2-validacion-de-codigo') {
          navigate('/olvide-contrasena/paso-2-validacion-de-codigo', { replace: true, state: null }); // redirigir
        }
        if (location.pathname === '/quiero-registrarme/paso-2-validacion-de-codigo') {
          navigate('/quiero-registrarme/paso-2-validacion-de-codigo', { replace: true, state: null }); // redirigir
        }
      }

      setHistoryIndex(currentIndex); // Actualiza el índice del historial
    };

    window.addEventListener("popstate", handlePopState);

    return () => {
      window.removeEventListener("popstate", handlePopState);
    };
  }, []);

  const handleChange = (
    index: number,
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const newOtp = [...otp];
    newOtp[index] = event.target.value;
    setOtp(newOtp);
    if (event.target.value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const pasteData = e.clipboardData.getData('text').slice(0, otp.length);
    const newOtp = [...otp];
    for (let i = 0; i < pasteData.length; i++) {
      if (/^[0-9]$/.test(pasteData[i])) {
        newOtp[i] = pasteData[i];
      }
    }
    setOtp(newOtp);
    inputRefs.current[pasteData.length - 1]?.focus();
  };

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if(dataVerifyOtp?.responseCode === "03" || dataVerifyOtp?.responseCode === "14" || dataVerifyOtp?.responseCode === "16"){
      resetOtp();
    }else{
      let formValido = true;
      const newErrors = [...errors];

      otp.forEach((value, index) => {
        if (value.trim() === "" || !/^\d+$/.test(value)) {
          newErrors[index] = true;
          formValido = false;
        } else {
          newErrors[index] = false;
        }
      });

      setErrors(newErrors);

      if (formValido) {
        mutateVerifyOtp({
          idRequest: payload.idRequest,
          otp: otp.join(""),
        });
      }
    }
  };
  const formatTime = (time: number) => {
    const minutos = Math.floor(time / 60);
    const segundos = time % 60;
    return `${minutos < 10 ? "0" : ""}${minutos}:${
      segundos < 10 ? "0" : ""
    }${segundos}`;
  };

  return (
    <>
      <div
        data-animation="default"
        data-collapse="medium"
        data-duration="400"
        data-easing="ease"
        data-easing2="ease"
        className="menu is-blanco w-nav"
      >
        <NavBarWithoutLogo />
      </div>
      {(isPendingVerifyOtp) && <Loading />}
      <section className="info-h">
        <div className="padding-global">
          <div className="padding-section-medium">
            <div className="container-medium">
              <div className="info_wrp">
                <div className="infor_form w-form">
                  <form
                    onSubmit={handleSubmit}
                    id="email-form-2"
                    name="email-form-2"
                    data-name="Email Form 2"
                    method="get"
                    className="form-paso-1"
                    data-wf-page-id="67226c83a99f39abdac5996c"
                    data-wf-element-id="a6ccd524-0406-410e-b27b-7cb6ad3096ca"
                  >
                    <BreadCrumb currentStep={2} />
                    <h2 className="text-align-center">
                      Te hemos enviado un código de <br />
                      verificación {payload?.verificationMethod === 1 ? `al correo ${payload?.email}` : `al teléfono ${payload?.phoneNumber}`}  
                    </h2>
                    <div className="hero_validation">
                      {otp.map((value, index) => (
                        <input
                          key={index}
                          ref={(el) => { inputRefs.current[index] = el; }}
                          className="hero_input w-input"
                          maxLength={1}
                          value={value}
                          onChange={(e) => handleChange(index, e)}
                          onPaste={handlePaste}
                          style={{
                            border: errors[index]
                              ? "2px solid red"
                              : "1px solid #ccc",
                          }}
                        />
                      ))}
                    </div>
                    <div className="text-align-center">
                      Ingresa el código de{" "}
                      <span className="text-weight-bold">6 dígitos</span>
                    </div>
                    <div className="hero_contador">
                      <div className="text-color-purpura text-align-center">
                        El código vencerá en:
                      </div>
                      <div className="hero_contador-flx">
                        <div
                          id="cuentaR"
                          className="text-color-purpura text-align-center"
                        >
                          {formatTime(timeRemaining)}
                        </div>
                        <div className="text-color-purpura text-align-center">
                          min
                        </div>
                      </div>
                      <div className="text-color-purpura text-align-center" style={{color: '#E52320'}}>
                        {messageError}
                      </div>
                    </div>
                    <div className="pasarella_btns">
                      <input
                        type="submit"
                        data-wait="Please wait..."
                        className="button is-large w-button"
                        value="Continuar"
                      />
                      <a
                        onClick={handleBack}
                        className="button is-large outline w-button"
                      >
                        Regresar
                      </a>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default OtpConfirmationPage;
